import { faChevronUp, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './Style.css';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import ModalTrigger from '../../../shared/modal/ModalTrigger';

type ClockProps = {
  startHour: number;
  startMinute: number;
  questionTitle: string;
  questionTitleSuffix?: string;
  modalText?: string;
  onSetValue: (value: { id: string; value: string }) => void;
  onOpenModal?: () => void;
  questionid: string;
  assessmentValues: Record<string, string>;
  width: string;
};

export function Clock({
  startHour,
  startMinute,
  questionTitle,
  questionTitleSuffix,
  modalText,
  onSetValue,
  onOpenModal,
  questionid,
  assessmentValues,
  width,
}: ClockProps) {
  const { t } = useTranslation();
  const [hourValue, setHourValue] = useState(startHour);
  const [minuteValue, setMinuteValue] = useState(startMinute);

  function setIncreaseClockHours() {
    let newHourValue;
    if (hourValue < 23) {
      newHourValue = Number(hourValue) + 1;
    } else {
      newHourValue = 0;
    }
    setHourValue(newHourValue);
    saveValue(`${newHourValue}:${minuteValue}`);
  }

  function setDecreaseClockHours() {
    let newHourValue = Number(hourValue);
    if (Number(hourValue) > 0) {
      newHourValue = Number(hourValue) - 1;
    } else {
      newHourValue = 23;
    }
    setHourValue(newHourValue);
    saveValue(`${newHourValue}:${minuteValue}`);
  }

  function setIncreaseClockMinutes() {
    let newMinuteValue = Number(minuteValue);
    if (Number(minuteValue) < 45) {
      newMinuteValue = Number(minuteValue) + 15;
    } else {
      newMinuteValue = 0;
    }
    setMinuteValue(newMinuteValue);
    saveValue(`${hourValue}:${newMinuteValue}`);
  }

  function setDecreaseClockMinutes() {
    let newMinuteValue = Number(minuteValue);
    if (Number(minuteValue) > 0) {
      newMinuteValue = Number(minuteValue) - 15;
      if (newMinuteValue === 0) {
        newMinuteValue = 0;
      }
    } else {
      newMinuteValue = 45;
    }
    setMinuteValue(newMinuteValue);
    saveValue(`${hourValue}:${newMinuteValue}`);
  }

  function clockChangeHandler(event) {
    if (event.target.name === 'hours') {
      setHourValue(event.target.value);
      saveValue(`${event.target.value}:${minuteValue}`);
    } else if (event.target.name === 'minutes') {
      setMinuteValue(event.target.value);
      saveValue(`${hourValue}:${event.target.value}`);
    }
  }

  const handleFocusOut = (event) => {
    if (event.target.id === 'hours') {
      let newHours = Number(event.target.value);
      if (newHours > 23) {
        newHours = 23;
      }
      if (newHours < 0 || !event.target.value || isNaN(newHours)) {
        newHours = 0;
      }

      setHourValue(newHours);
      saveValue(`${newHours}:${minuteValue}`);
    } else if (event.target.id === 'minutes') {
      let newMins = Number(event.target.value);
      if (newMins > 45) {
        newMins = 45;
      }
      if (newMins < 0 || !event.target.value || isNaN(newMins)) {
        newMins = 0;
      }
      setMinuteValue(newMins);
      saveValue(`${hourValue}:${newMins}`);
    }
  };

  function saveValue(time) {
    onSetValue({ id: questionid, value: time });
  }
  useEffect(() => {
    if (assessmentValues && assessmentValues[questionid]) {
      const time = assessmentValues[questionid];
      const index = time.indexOf(':');
      const hour = time.substr(0, index);
      const minute = time.substr(index + 1);
      setHourValue(Number(hour));
      setMinuteValue(Number(minute));
    } else if (startMinute === minuteValue && startHour === hourValue) {
      saveValue(`${hourValue}:${minuteValue}`);
    }
  });

  return (
    <div className={`question_block${width} clock_container`}>
      <fieldset id={questionid} data-input-type="clock">
        <legend className="assessment_question_title">{questionTitle}</legend>
        {questionTitleSuffix ? <span className="titleSuffix">{questionTitleSuffix}</span> : null}
        {modalText && (
          <ModalTrigger
            modalText={modalText}
            onOpenModal={onOpenModal}
            question={questionid}
            questionTitle={questionTitle}
          />
        )}
        <div className="answer_block">
          <div className="clock">
            <div className="clock_padding">
              <label className="titleSuffix" htmlFor={`${questionid}_hours`}>
                {t('A132')}
              </label>
              <div className="clock_inputs">
                <input
                  type="number"
                  className="hours fs-exclude"
                  id={`${questionid}_hours`}
                  name="hours"
                  value={hourValue}
                  onChange={clockChangeHandler}
                  onBlur={handleFocusOut}
                  max="24"
                  min="00"
                  maxLength={2}
                />
                <div className="number_controls">
                  <button
                    type="button"
                    className="up_btn"
                    onClick={setIncreaseClockHours}
                    aria-labelledby={`${questionid}_hours_up`}
                  >
                    <span
                      className="visuallyHidden"
                      id={`${questionid}_hours_up`}
                      aria-hidden="true"
                    >
                      {t('A134')} {t('A132')}
                    </span>
                    <FontAwesomeIcon icon={faChevronUp} className="icon" />
                  </button>
                  <button
                    type="button"
                    className="down_btn"
                    onClick={setDecreaseClockHours}
                    aria-labelledby={`${questionid}_hours_down`}
                  >
                    <span
                      className="visuallyHidden"
                      id={`${questionid}_hours_down`}
                      aria-hidden="true"
                    >
                      {t('A135')} {t('A132')}
                    </span>
                    <FontAwesomeIcon icon={faChevronDown} className="icon" />
                  </button>
                </div>
              </div>
            </div>
            <div className="clock_padding">
              <label className="titleSuffix" htmlFor={`${questionid}minutes`}>
                {t('A131')}
              </label>
              <div className="clock_inputs">
                <input
                  type="number"
                  className="minutes fs-exclude"
                  id={`${questionid}minutes`}
                  name="minutes"
                  value={minuteValue}
                  onChange={clockChangeHandler}
                  onBlur={handleFocusOut}
                  step="15"
                  max="45"
                  min="00"
                  maxLength={2}
                />
                <div className="number_controls">
                  <button
                    type="button"
                    className="up_btn"
                    onClick={setIncreaseClockMinutes}
                    aria-labelledby={`${questionid}_minutes_up`}
                  >
                    <span
                      className="visuallyHidden"
                      id={`${questionid}_minutes_up`}
                      aria-hidden="true"
                    >
                      {t('A134')} {t('A131')}
                    </span>
                    <FontAwesomeIcon icon={faChevronUp} className="icon" />
                  </button>
                  <button
                    type="button"
                    className="down_btn"
                    onClick={setDecreaseClockMinutes}
                    aria-labelledby={`${questionid}_minutes_down`}
                  >
                    <span
                      className="visuallyHidden"
                      id={`${questionid}_minutes_down`}
                      aria-hidden="true"
                    >
                      {t('A135')} {t('A131')}
                    </span>
                    <FontAwesomeIcon icon={faChevronDown} className="icon" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </fieldset>
    </div>
  );
}
